import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import type { PaginatedRequest, PaginatedResponse } from '@/types';

export interface LogisticOperatorWithCommerce {
  id: string;
  logisticOperatorId: string;
  commerceId: string;
  logisticOperator: {
    id: string;
    name: string;
    businessName: string;
    taxNumber: string;
    status: string;
    inteliPath?: string | null;
  };
  commerce: {
    id: string;
    name: string;
    businessName: string;
    status: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LogisticOperatorAssociationFilters {
  commerceId?: string;
  logisticOperatorId?: string;
}

export interface CreateLogisticOperatorAssociation {
  logisticOperatorId: string;
  commerceId: string;
}

const logisticOperatorAssociationService = createApi({
  reducerPath: 'logisticOperatorAssociationService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['LogisticOperatorAssociation'],
  endpoints: (builder) => ({
    getLogisticOperatorAssociations: builder.query<
      PaginatedResponse<LogisticOperatorWithCommerce>,
      {
        paginated: PaginatedRequest;
        filters?: LogisticOperatorAssociationFilters;
      }
    >({
      query: ({ paginated, filters }) => {
        const params: Record<string, any> = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          include: 'commerce,logisticOperator',
        };

        if (filters?.commerceId) {
          params.commerceId = filters.commerceId;
        }
        if (filters?.logisticOperatorId) {
          params.logisticOperatorId = filters.logisticOperatorId;
        }

        return {
          url: '/logistic-operators-with-commerce',
          params,
        };
      },
      providesTags: ['LogisticOperatorAssociation'],
    }),
    getLogisticOperatorByCommerce: builder.query<
      LogisticOperatorWithCommerce[],
      { commerceId: string }
    >({
      query: ({ commerceId }) => ({
        url: `/logistic-operators-with-commerce/commerce/${commerceId}`,
      }),
      providesTags: ['LogisticOperatorAssociation'],
    }),
    getLogisticOperatorAssociationById: builder.query<
      LogisticOperatorWithCommerce,
      { associationId: string }
    >({
      query: ({ associationId }) => ({
        url: `/logistic-operators-with-commerce/${associationId}`,
      }),
      providesTags: ['LogisticOperatorAssociation'],
    }),
    createLogisticOperatorAssociation: builder.mutation<
      LogisticOperatorWithCommerce,
      CreateLogisticOperatorAssociation
    >({
      query: (body) => ({
        url: '/logistic-operators-with-commerce',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LogisticOperatorAssociation'],
    }),
    deleteLogisticOperatorAssociation: builder.mutation<
      void,
      { associationId: string }
    >({
      query: ({ associationId }) => ({
        url: `/logistic-operators-with-commerce/${associationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LogisticOperatorAssociation'],
    }),
  }),
});

export default logisticOperatorAssociationService;
export const {
  useGetLogisticOperatorAssociationsQuery,
  useGetLogisticOperatorByCommerceQuery,
  useGetLogisticOperatorAssociationByIdQuery,
  useCreateLogisticOperatorAssociationMutation,
  useDeleteLogisticOperatorAssociationMutation,
} = logisticOperatorAssociationService;
